import { useEffect, useRef, useState } from "react";
import { StaffColor } from "../lib/staff-colors.ts";
import type { FormatPrefs } from "../lib/i18n.ts";
import { formatTime as fmtTimeI18n } from "../lib/i18n.ts";
import { EventTooltip } from "./EventTooltip.tsx";
import { decodeEntities } from "../lib/decode-entities.ts";

export interface CalendarEventAppt {
  id: string;
  start_at: string;
  end_at: string;
  status: string;
  customer_name: string;
  customer_email?: string | null;
  customer_phone?: string | null;
  service_name: string;
  staff_id: string;
  staff_name: string;
  notes?: string | null;
}

interface Props {
  appt: CalendarEventAppt;
  height: number; // px height of the event block, for layout decisions
  color?: StaffColor;
  /** Per-service accent hex (#RRGGBB) — overrides the staff colour border. */
  accentHex?: string;
  /** Click handler — opens AppointmentDetailsModal with the full record. */
  onSelect?: (id: string) => void;
  /** Render the bottom-edge resize handle. */
  showResizeHandle?: boolean;
  /** Locale + 12/24-hour format. Defaults to browser-locale 12-hour. */
  formatPrefs?: FormatPrefs;
}

function formatTime(iso: string, prefs?: FormatPrefs): string {
  if (prefs) return fmtTimeI18n(iso, prefs);
  return new Date(iso).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
}

function statusStyle(status: string): { bg: string; text: string; label: string } {
  const map: Record<string, { bg: string; text: string; label: string }> = {
    confirmed: { bg: "var(--status-confirmed-bg)", text: "var(--status-confirmed)", label: "Confirmed" },
    tentative: { bg: "var(--status-tentative-bg)", text: "var(--status-tentative)", label: "Tentative" },
    completed: { bg: "var(--status-done-bg)", text: "var(--status-done)", label: "Complete" },
    no_show: { bg: "var(--status-noshow-bg)", text: "var(--status-noshow)", label: "No-show" },
    cancelled: { bg: "var(--status-cancelled-bg)", text: "var(--status-cancelled)", label: "Cancelled" },
  };
  return map[status] ?? { bg: "var(--paper-well)", text: "var(--ink-soft)", label: status };
}

function statusAbbrev(status: string): string {
  const map: Record<string, string> = {
    confirmed: "CONF",
    tentative: "TENT",
    completed: "DONE",
    no_show: "NO-SHOW",
    cancelled: "CANC",
  };
  return map[status] ?? status.toUpperCase();
}

/**
 * Detect coarse pointer (touch device) — disable hover tooltip there.
 * Tap → opens details modal in B1; that's the touch-equivalent.
 */
function useIsCoarsePointer(): boolean {
  const [coarse, setCoarse] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(pointer: coarse)");
    const update = () => setCoarse(mq.matches);
    update();
    mq.addEventListener("change", update);
    return () => mq.removeEventListener("change", update);
  }, []);
  return coarse;
}

export function CalendarEvent({ appt, height, color, accentHex, onSelect, showResizeHandle, formatPrefs }: Props) {
  const isCompleted = appt.status === "completed";
  const isNoShow = appt.status === "no_show";
  const isCancelled = appt.status === "cancelled";
  const status = statusStyle(appt.status);
  const isCoarse = useIsCoarsePointer();
  const ref = useRef<HTMLDivElement>(null);
  const [tooltipAnchor, setTooltipAnchor] = useState<DOMRect | null>(null);
  const tooltipTimer = useRef<number | null>(null);

  const onMouseEnter = () => {
    if (isCoarse) return;
    if (tooltipTimer.current) window.clearTimeout(tooltipTimer.current);
    tooltipTimer.current = window.setTimeout(() => {
      if (ref.current) setTooltipAnchor(ref.current.getBoundingClientRect());
    }, 200);
  };
  const onMouseLeave = () => {
    if (tooltipTimer.current) window.clearTimeout(tooltipTimer.current);
    tooltipTimer.current = null;
    setTooltipAnchor(null);
  };
  useEffect(
    () => () => {
      if (tooltipTimer.current) window.clearTimeout(tooltipTimer.current);
    },
    [],
  );

  // Layout-density tiers based on rendered height. Thresholds account for
  // the card's font sizes (meta 12, name 13, pill ~14) + 12px padding.
  // 'tiny'   < 36px  → name only
  // 'small'  < 52px  → meta + name
  // 'medium' ≥ 52px  → meta + name + status pill
  const tier = height < 36 ? "tiny" : height < 52 ? "small" : "medium";

  return (
    <>
    <div
      ref={ref}
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
      onClick={onSelect ? () => onSelect(appt.id) : undefined}
      style={{
        position: "relative",
        height: "100%",
        // When a service accent is set, blend it lightly into the background;
        // otherwise fall back to the staff-tinted background.
        background: accentHex
          ? `color-mix(in oklab, ${accentHex} 8%, var(--paper-raised))`
          : color?.bg ?? "var(--accent-soft)",
        borderLeft: `3px solid ${
          isNoShow
            ? "var(--status-noshow)"
            : isCancelled
              ? "var(--status-cancelled)"
              : accentHex ?? color?.border ?? "var(--accent)"
        }`,
        borderRadius: 8,
        padding: tier === "tiny" ? "2px 6px" : "6px 8px",
        overflow: "hidden",
        opacity: isCompleted ? 0.55 : isCancelled ? 0.5 : 1,
        textDecoration: isNoShow ? "line-through" : undefined,
        textDecorationColor: isNoShow ? "var(--status-noshow)" : undefined,
        cursor: onSelect ? "pointer" : "default",
        display: "flex",
        flexDirection: "column",
        gap: tier === "tiny" ? 0 : 2,
      }}
    >
      {tier !== "tiny" && (
        <div
          style={{
            fontSize: 12,
            color: color?.text ?? "var(--accent-ink)",
            opacity: 0.75,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            fontVariantNumeric: "tabular-nums",
            paddingRight: 56,
          }}
        >
          {formatTime(appt.start_at, formatPrefs)}–{formatTime(appt.end_at, formatPrefs)} · {decodeEntities(appt.service_name)}
        </div>
      )}

      {tier === "tiny" ? (
        <span
          aria-label={status.label}
          style={{
            position: "absolute",
            top: 6,
            right: 6,
            width: 8,
            height: 8,
            borderRadius: 999,
            background: status.text,
          }}
        />
      ) : (
        <span
          style={{
            position: "absolute",
            top: 4,
            right: 4,
            fontSize: 9.5,
            fontWeight: 600,
            padding: "1px 5px",
            borderRadius: 999,
            background: status.bg,
            color: status.text,
            letterSpacing: "0.03em",
            textTransform: "uppercase",
            lineHeight: 1.4,
            pointerEvents: "none",
          }}
        >
          {statusAbbrev(appt.status)}
        </span>
      )}
      <div
        style={{
          fontSize: 13,
          fontWeight: 600,
          color: color?.text ?? "var(--accent-ink)",
          lineHeight: 1.2,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          paddingRight: tier === "tiny" ? 14 : 56,
        }}
      >
        {decodeEntities(appt.customer_name)}
      </div>
      {showResizeHandle && (
        <div
          aria-label="Resize"
          style={{
            position: "absolute",
            left: 8,
            right: 8,
            bottom: 0,
            // 12px on touch (coarse pointer), 8px on mouse — bigger touch
            // target per WCAG 44×44 guidance, but the handle itself is just
            // an edge of the card so total touch area extends upward.
            height: isCoarse ? 12 : 8,
            cursor: "ns-resize",
            background:
              "linear-gradient(transparent 50%, color-mix(in oklab, var(--ink) 12%, transparent) 50%)",
            backgroundSize: isCoarse ? "100% 6px" : "100% 4px",
            backgroundRepeat: "no-repeat",
            backgroundPosition: "center",
            opacity: 0.5,
          }}
        />
      )}
    </div>
    {tooltipAnchor && <EventTooltip appt={appt} anchor={tooltipAnchor} formatPrefs={formatPrefs} />}
    </>
  );
}
